import { NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft } from 'merge-left-utils'
import useSearchParams from '@@partner:helpers/hooks/useSearchParams'

import { Button, CheckPicker, Form, Input, Tag } from 'rsuite'
import LegacySearchIcon from '@rsuite/icons/legacy/Search'

import { CONTAINER_SIZES, CONTAINER_TYPES } from '../../../constants/storageContainer'

import './StorageContainersFilter.scss'

interface StorageContainersFilterState {
  container_id: string
  sizes: string[]
  types: string[]
  is_active: boolean | null
  description: string
}

type StorageContainersFilterProps = {
  filter: NestedFilter
  onFilterChanged: (filter: NestedFilter) => void
}

const defaultFilter: StorageContainersFilterState = {
  container_id: '',
  sizes: [],
  types: [],
  is_active: null,
  description: ''
}

const ACTIVE_STATUS_OPTIONS = [
  { label: 'Активные', value: 'true', color: 'green' },
  { label: 'Неактивные', value: 'false', color: 'red' }
]

export default function StorageContainersFilter ({ filter, onFilterChanged }: StorageContainersFilterProps) {
  const { getSearchParams, setSearchParams } = useSearchParams()
  const [ state, setState ] = useState<StorageContainersFilterState>(defaultFilter)

  useEffect(() => {
    const searchParams = getSearchParams()
    const newState = mergeLeft(defaultFilter, searchParams)
    setState(newState)
    onFilterChanged(createFilter(newState))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const createFilter = useCallback((filterState: StorageContainersFilterState): NestedFilter => {
    const conditions: NestedFilter[] = []

    if (filterState.container_id) {
      conditions.push({
        fields: [ 'container_id' ],
        op: 'includes',
        value: [ filterState.container_id ]
      })
    }

    if (filterState.sizes.length > 0) {
      conditions.push({
        fields: [ 'size' ],
        op: 'in',
        value: filterState.sizes
      })
    }

    if (filterState.types.length > 0) {
      conditions.push({
        fields: [ 'type' ],
        op: 'in',
        value: filterState.types
      })
    }

    if (filterState.is_active !== null) {
      conditions.push({
        fields: [ 'is_active' ],
        op: 'eq',
        value: [ filterState.is_active ? 1 : 0 ]
      })
    }

    if (filterState.description) {
      conditions.push({
        fields: [ 'description' ],
        op: 'contains',
        value: [ filterState.description ]
      })
    }

    if (conditions.length === 0) {
      return {
        fields: [ 'id' ],
        op: 'unwrap'
      }
    }

    if (conditions.length === 1) {
      return conditions[0]
    }

    return {
      and: conditions
    }
  }, [])

  const handleFormSubmit = useCallback(() => {
    setSearchParams(state)
    onFilterChanged(createFilter(state))
  }, [ state, createFilter, onFilterChanged, setSearchParams ])

  const handleSizesChange = useCallback((sizes: string[] = []) => {
    setState((state) => ({
      ...state,
      sizes
    }))
  }, [])

  const handleTypesChange = useCallback((types: string[] = []) => {
    setState((state) => ({
      ...state,
      types
    }))
  }, [])

  const handleActiveStatusChange = useCallback((statuses: string[] = []) => {
    setState((state) => ({
      ...state,
      is_active: statuses.length === 0 
        ? null 
        : statuses.includes('true') 
          ? true 
          : false
    }))
  }, [])

  const handleContainerIdChange = useCallback((value: string) => {
    setState((state) => ({
      ...state,
      container_id: value
    }))
  }, [])

  const handleDescriptionChange = useCallback((value: string) => {
    setState((state) => ({
      ...state,
      description: value
    }))
  }, [])

  return (
    <Form
      formValue={state}
      className="StorageContainersFilter"
      onSubmit={handleFormSubmit}
    >
      <div className="StorageContainersFilter__Filters">
        <Form.Group>
          <Input
            value={state.container_id}
            onChange={handleContainerIdChange}
            placeholder="Код контейнера"
            size="sm"
            style={{ minWidth: 200 }}
          />

          <Input
            value={state.description}
            onChange={handleDescriptionChange}
            placeholder="Описание"
            size="sm"
            style={{ minWidth: 200 }}
          />

          <CheckPicker
            value={state.sizes}
            data={CONTAINER_SIZES}
            searchable={false}
            placeholder="Размер"
            valueKey="value"
            labelKey="label"
            size="sm"
            style={{ minWidth: 180 }}
            onChange={handleSizesChange}
          />

          <CheckPicker
            value={state.types}
            data={CONTAINER_TYPES}
            searchable={false}
            placeholder="Тип"
            valueKey="value"
            labelKey="label"
            size="sm"
            style={{ minWidth: 200 }}
            onChange={handleTypesChange}
          />

          <CheckPicker
            value={state.is_active === null ? [] : [ state.is_active ? 'true' : 'false' ]}
            data={ACTIVE_STATUS_OPTIONS}
            searchable={false}
            placeholder="Статус"
            valueKey="value"
            labelKey="label"
            size="sm"
            style={{ minWidth: 180 }}
            renderMenuItem={(label, item: any) => (
              <Tag key={item?.value} color={item?.color}>
                {label}
              </Tag>
            )}
            onChange={handleActiveStatusChange}
          />
        </Form.Group>
      </div>

      <div className="StorageContainersFilter__Controls">
        <Button onClick={handleFormSubmit} size="lg" color="green" appearance="primary">
          <LegacySearchIcon /> Поиск
        </Button>
      </div>
    </Form>
  )
}
